/**
 * TimeSeriesBarChart — a config-driven Recharts `ComposedChart` for time-series
 * bars with optional overlays: multiple bar series (each pinned to the left or
 * right axis), an optional smoothed trend line, and an optional mean reference
 * line. One presentational component covers both the Overview combo (consumption
 * bars + data-quality bars + trend + mean) and the plain daily-consumption bar,
 * so the two stay visually consistent. Purely presentational — the caller shapes
 * the data (see features/sensors/series.ts).
 */
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { chartColors } from "@/lib/tokens";

import { tooltipContentStyle } from "./chartTheme";

export interface BarSeries {
  key: string;
  name: string;
  color: string;
  axis?: "left" | "right";
  opacity?: number;
}
export interface TrendSpec {
  key: string;
  name: string;
  color?: string;
  axis?: "left" | "right";
}
export interface MeanSpec {
  value: number;
  label: string;
  color?: string;
}
interface AxisSpec {
  domain?: [number, number];
  width?: number;
}

export interface TimeSeriesBarChartProps {
  /** Row objects keyed by `xKey` / bar keys (Recharts reads keys by name). */
  data: object[];
  xKey: string;
  bars: BarSeries[];
  trend?: TrendSpec;
  meanLine?: MeanSpec;
  leftAxis?: AxisSpec;
  rightAxis?: AxisSpec;
  height?: number | string;
  ariaLabel?: string;
}

export function TimeSeriesBarChart({
  data,
  xKey,
  bars,
  trend,
  meanLine,
  leftAxis,
  rightAxis,
  height = 320,
  ariaLabel,
}: TimeSeriesBarChartProps) {
  const hasRight = !!rightAxis || bars.some((b) => b.axis === "right") || trend?.axis === "right";
  // A second bar series (typically a different unit sharing the chart, e.g.
  // data quality % beside consumption m³) or a trend overlay is otherwise
  // unlabeled outside the hover tooltip — print it instead of hiding it there.
  const needsLegend = bars.length > 1 || !!trend;
  return (
    <div role="img" aria-label={ariaLabel} className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: needsLegend ? 20 : 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={chartColors.border(0.6)} vertical={false} />
          {needsLegend && (
            <Legend
              verticalAlign="top"
              align="right"
              height={28}
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11, color: chartColors.fgMuted() }}
            />
          )}
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 10, fill: chartColors.fgMuted() }}
            minTickGap={24}
            tickLine={false}
            axisLine={{ stroke: chartColors.border() }}
          />
          <YAxis
            yAxisId="left"
            domain={leftAxis?.domain}
            width={leftAxis?.width ?? 44}
            tick={{ fontSize: 11, fill: chartColors.fgMuted() }}
            tickLine={false}
            axisLine={false}
          />
          {hasRight && (
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={rightAxis?.domain ?? [0, 100]}
              width={rightAxis?.width ?? 36}
              tick={{ fontSize: 11, fill: chartColors.fgMuted() }}
              tickLine={false}
              axisLine={false}
            />
          )}
          <Tooltip contentStyle={tooltipContentStyle} />
          {bars.map((b) => (
            <Bar
              key={b.key}
              yAxisId={b.axis === "right" ? "right" : "left"}
              dataKey={b.key}
              name={b.name}
              fill={b.color}
              fillOpacity={b.opacity ?? 1}
              radius={[3, 3, 0, 0]}
            />
          ))}
          {meanLine && (
            <ReferenceLine
              yAxisId="left"
              y={meanLine.value}
              stroke={meanLine.color ?? chartColors.fgMuted()}
              strokeDasharray="5 4"
              label={{
                value: meanLine.label,
                position: "insideTopLeft",
                fontSize: 10,
                fill: chartColors.fgMuted(),
              }}
            />
          )}
          {trend && (
            <Line
              yAxisId={trend.axis === "right" ? "right" : "left"}
              type="monotone"
              dataKey={trend.key}
              name={trend.name}
              stroke={trend.color ?? chartColors.critical()}
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={false}
              connectNulls
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
